import type { ManagerData } from "@/hooks/useDashboardData";

interface Props {
  data: ManagerData;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return (parts[0]?.charAt(0) ?? "?").toUpperCase();
  return ((parts[0]?.charAt(0) ?? "") + (parts[parts.length - 1]?.charAt(0) ?? "")).toUpperCase();
}

function daysSince(iso: string): number {
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

export function DashboardManagerView({ data }: Props): JSX.Element {
  const { funnel, actionQueues, completionBySim, timeToComplete, dropOffMap } = data;
  const needAttention = actionQueues.notStarted.total + actionQueues.stuck.total + actionQueues.atRisk.total;
  const assigned = funnel.assigned;
  const conv = (n: number): number => assigned > 0 ? Math.round((n / assigned) * 100) : 0;

  const maxMinutes = Math.max(1, ...timeToComplete.map((t) => t.medianMinutes ?? 0));

  return (
    <>
      <div className="lq-glass lq-glass-a" style={{ padding: "20px 22px", marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "0.9fr 2.1fr", gap: 22, alignItems: "center" }}>
          <div>
            <div className="lq-eyebrow" style={{ marginBottom: 10 }}>NEED ATTENTION</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <div className="lq-stat-num" style={{ color: "#FCD34D" }}>{needAttention}</div>
              <div className="lq-stat-sub">learners</div>
            </div>
            <div style={{ fontSize: 11, color: "#71717A", marginTop: 8, lineHeight: 1.5 }}>
              {assigned > 0 ? Math.round((needAttention / assigned) * 100) : 0}% of cohort needs action this week.
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(239,68,68,0.22)", background: "rgba(239,68,68,0.06)" }}>
              <div className="lq-eyebrow" style={{ color: "#FCA5A5" }}>NOT STARTED</div>
              <div style={{ fontSize: 22, fontWeight: 500, marginTop: 4, fontVariantNumeric: "tabular-nums", color: "#FCA5A5" }}>{actionQueues.notStarted.total}</div>
              <div style={{ fontSize: 10, color: "#71717A", marginTop: 2 }}>Send invite</div>
            </div>
            <div style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(245,158,11,0.22)", background: "rgba(245,158,11,0.06)" }}>
              <div className="lq-eyebrow" style={{ color: "#FCD34D" }}>STUCK · 7+ DAYS</div>
              <div style={{ fontSize: 22, fontWeight: 500, marginTop: 4, fontVariantNumeric: "tabular-nums", color: "#FCD34D" }}>{actionQueues.stuck.total}</div>
              <div style={{ fontSize: 10, color: "#71717A", marginTop: 2 }}>Nudge</div>
            </div>
            <div style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(239,68,68,0.22)", background: "rgba(239,68,68,0.06)" }}>
              <div className="lq-eyebrow" style={{ color: "#FCA5A5" }}>AT RISK</div>
              <div style={{ fontSize: 22, fontWeight: 500, marginTop: 4, fontVariantNumeric: "tabular-nums", color: "#FCA5A5" }}>{actionQueues.atRisk.total}</div>
              <div style={{ fontSize: 10, color: "#71717A", marginTop: 2 }}>Intervene</div>
            </div>
          </div>
        </div>
      </div>

      <div className="lq-glass" style={{ padding: "18px 20px", marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Learner funnel</div>
        </div>
        <div style={{ fontSize: 11, color: "#71717A", marginBottom: 14 }}>Where the {assigned} learner{assigned === 1 ? "" : "s"} are right now</div>
        <div>
          <FunnelRow label="Assigned" count={funnel.assigned} pct={100}
                     fill="linear-gradient(to right, rgba(255,255,255,0.18), rgba(255,255,255,0.3))" />
          <FunnelRow label="Started" count={funnel.started} pct={conv(funnel.started)}
                     fill="linear-gradient(to right, rgba(52,211,153,0.4), #34D399)" glow="rgba(52,211,153,0.2)" />
          <FunnelRow label="In progress" count={funnel.inProgress} pct={conv(funnel.inProgress)}
                     fill="linear-gradient(to right, rgba(251,191,36,0.4), #FBBF24)" glow="rgba(251,191,36,0.18)" />
          <FunnelRow label="Completed" count={funnel.completed} pct={conv(funnel.completed)}
                     fill="linear-gradient(to right, rgba(16,185,129,0.5), #10B981)" glow="rgba(16,185,129,0.25)" />
          <FunnelRow label="Not started" count={funnel.notStarted} pct={conv(funnel.notStarted)}
                     fill="linear-gradient(to right, rgba(248,113,113,0.35), #F87171)" glow="rgba(248,113,113,0.18)" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
        <ActionQueue
          variant="r"
          eyebrow="NOT STARTED"
          total={actionQueues.notStarted.total}
          ctaLabel="Invite all"
          rows={actionQueues.notStarted.rows.map((r) => ({
            initials: initials(r.fullName),
            name: r.fullName,
            sub: `${r.cohortName} · assigned ${daysSince(r.assignedAt)}d ago`,
          }))}
        />
        <ActionQueue
          variant="a"
          eyebrow="STUCK · 7+ DAYS"
          total={actionQueues.stuck.total}
          ctaLabel="Nudge all"
          rows={actionQueues.stuck.rows.map((r) => ({
            initials: initials(r.fullName),
            name: r.fullName,
            sub: `${r.cohortName} · idle ${r.idleDays}d`,
          }))}
        />
        <ActionQueue
          variant="r"
          eyebrow="AT RISK · NEEDS WORK"
          total={actionQueues.atRisk.total}
          ctaLabel="Flag"
          rows={actionQueues.atRisk.rows.map((r) => ({
            initials: initials(r.fullName),
            name: r.fullName,
            sub: `${r.cohortName} · avg ${r.avgScore?.toFixed(1) ?? "—"} · ${r.simCount} sim${r.simCount === 1 ? "" : "s"}`,
          }))}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div className="lq-glass" style={{ padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Completion by sim</div>
            <div style={{ fontSize: 10, color: "#71717A" }}>% of assigned</div>
          </div>
          {completionBySim.length === 0 ? (
            <div style={{ fontSize: 12, color: "#71717A", padding: "20px 0" }}>No simulations assigned to this client yet.</div>
          ) : (
            completionBySim.map((s) => {
              const variant = s.completionPct >= 70 ? "e" : s.completionPct >= 50 ? "a" : "r";
              return (
                <div key={s.simulationId} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 40px", gap: 10, alignItems: "center", padding: "7px 0" }}>
                  <div style={{ fontSize: 11, color: "#D4D4D8" }}>{s.simTitle}</div>
                  <div className="lq-track"><div className={`lq-track-fill lq-track-fill-${variant}`} style={{ width: `${s.completionPct}%` }} /></div>
                  <div style={{ fontSize: 12, fontWeight: 500, textAlign: "right", fontVariantNumeric: "tabular-nums", color: variant === "r" ? "#FCA5A5" : undefined }}>{s.completionPct}%</div>
                </div>
              );
            })
          )}
        </div>

        <div className="lq-glass" style={{ padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Median time-to-complete</div>
            <div style={{ fontSize: 10, color: "#71717A" }}>minutes · whole sim</div>
          </div>
          {timeToComplete.length === 0 ? (
            <div style={{ fontSize: 12, color: "#71717A", padding: "20px 0" }}>No completed attempts yet.</div>
          ) : (
            timeToComplete.map((t) => (
              <div key={t.simulationId} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 50px", gap: 10, alignItems: "center", padding: "7px 0" }}>
                <div style={{ fontSize: 11, color: "#D4D4D8" }}>{t.simTitle}</div>
                <div className="lq-track">
                  <div className="lq-track-fill lq-track-fill-v" style={{ width: t.medianMinutes !== null ? `${Math.min(100, (t.medianMinutes / maxMinutes) * 100)}%` : "0%" }} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 500, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {t.medianMinutes !== null ? `${Math.round(t.medianMinutes)}m` : "—"}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="lq-glass" style={{ padding: "16px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Drop-off map</div>
          <div style={{ fontSize: 10, color: "#71717A" }}>% who reached each decision · cliff = painful node</div>
        </div>
        <div style={{ fontSize: 11, color: "#71717A", marginBottom: 14 }}>Where learners give up — D1 (left) to last (right)</div>
        {dropOffMap.length === 0 ? (
          <div style={{ fontSize: 12, color: "#71717A", padding: "20px 0" }}>No data yet — need at least one attempt per sim.</div>
        ) : (
          dropOffMap.map((s) => <DropOffRow key={s.simulationId} sim={s} />)
        )}
      </div>
    </>
  );
}

function FunnelRow({ label, count, pct, fill, glow }: { label: string; count: number; pct: number; fill: string; glow?: string }): JSX.Element {
  return (
    <div className="lq-funnel-row">
      <div className="lq-funnel-label">{label}</div>
      <div className="lq-funnel-track">
        <div className="lq-funnel-fill" style={{ width: `${Math.min(100, pct)}%`, background: fill, boxShadow: glow !== undefined ? `0 0 12px ${glow}` : undefined }} />
      </div>
      <div className="lq-funnel-count">{count}</div>
      <div className="lq-funnel-conv">{pct}%</div>
    </div>
  );
}

interface ActionQueueProps {
  variant: "a" | "r";
  eyebrow: string;
  total: number;
  ctaLabel: string;
  rows: Array<{ initials: string; name: string; sub: string }>;
}

function ActionQueue({ variant, eyebrow, total, ctaLabel, rows }: ActionQueueProps): JSX.Element {
  const tone = variant === "a" ? { color: "#FCD34D", avatar: "lq-avatar-a" } : { color: "#FCA5A5", avatar: "lq-avatar-r" };
  const glassClass = variant === "a" ? "lq-glass lq-glass-a" : "lq-glass lq-glass-r";
  return (
    <div className={glassClass} style={{ padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div>
          <div className="lq-eyebrow" style={{ color: tone.color }}>{eyebrow}</div>
          <div style={{ fontSize: 28, fontWeight: 500, lineHeight: 1, fontVariantNumeric: "tabular-nums", letterSpacing: -0.5, color: tone.color }}>{total}</div>
        </div>
        <button className="lq-btn-mini">{ctaLabel}</button>
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 11, color: "#71717A", padding: "10px 0" }}>None.</div>
      ) : (
        <div>
          {rows.map((r) => (
            <div key={r.name} className="lq-action-row">
              <div className={`lq-avatar ${tone.avatar}`}>{r.initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11 }}>{r.name}</div>
                <div style={{ fontSize: 9, color: "#71717A" }}>{r.sub}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {total > rows.length && (
        <button className="lq-btn-mini" style={{ width: "100%", marginTop: 8 }}>See all {total}</button>
      )}
    </div>
  );
}

function DropOffRow({ sim }: { sim: ManagerData["dropOffMap"][number] }): JSX.Element {
  const max = sim.maxSequence ?? 0;
  if (max === 0 || sim.reached.length === 0) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 40px", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <div style={{ fontSize: 11, color: "#D4D4D8" }}>{sim.simTitle}</div>
        <div style={{ fontSize: 10, color: "#71717A" }}>no attempts</div>
        <div />
      </div>
    );
  }
  const peak = Math.max(...sim.reached.map((r) => r.n));
  const W = 300, H = 36;
  const points = sim.reached.map((r) => {
    const x = max > 1 ? ((r.seq - 1) / (max - 1)) * W : W / 2;
    const y = H - ((r.n / peak) * (H - 4)) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  // Cliff detection: biggest drop between adjacent decisions
  let biggestDrop = 0;
  let cliffSeq: number | null = null;
  for (let i = 1; i < sim.reached.length; i++) {
    const prev = sim.reached[i - 1]!.n;
    const cur = sim.reached[i]!.n;
    const drop = prev > 0 ? (prev - cur) / prev : 0;
    if (drop > biggestDrop) { biggestDrop = drop; cliffSeq = sim.reached[i]!.seq; }
  }
  const stroke = biggestDrop > 0.15 ? "#F87171" : biggestDrop > 0.08 ? "#FBBF24" : "#34D399";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 40px", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <div style={{ fontSize: 11, color: "#D4D4D8" }}>{sim.simTitle}</div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: H }} aria-hidden="true">
        <polyline points={points} fill="none" stroke={stroke} strokeWidth={1.4} />
        {cliffSeq !== null && biggestDrop > 0.08 && (
          <g>
            <circle cx={max > 1 ? ((cliffSeq - 1) / (max - 1)) * W : W / 2} cy={H / 2} r={3} fill="#EF4444" />
          </g>
        )}
      </svg>
      <div style={{ fontSize: 10, color: "#71717A", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>D{max}</div>
    </div>
  );
}
